import { useEffect, useState } from "react";
import { PublicKey, Transaction } from "@solana/web3.js";
import { BN } from "bn.js";
import { useConnection } from "@solana/wallet-adapter-react";
import clsx from "clsx";
import { Trash } from "react-feather";
import BigNumber from "bignumber.js";
import { getAssociatedTokenAddressSync } from "@solana/spl-token";
import Button from "@axvdex/components/common/Button";
import { DigitalAsset, OutbidAsset, Sale, SaleStatus } from "@axvdex/types/solana/outbid";
import { useAppDispatch } from "@axvdex/state";
import useLanguage from "@axvdex/hooks/useLanguage";
import imgSanitize from "@axvdex/utils/imgSanitize";
import CustomSelect from "@axvdex/components/form-element/CustomSelect";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import {
  createAddSoldOutBidderIncentivesInstruction,
  createAddSpecificBidsIncentivesInstruction,
  errorFromCode,
} from "@axvdex/solana/outbidAPI/js/generated";
import {
  findSoldOutBidderIncentives,
  findSpecificBidsIncentives,
  findTransferAuthority,
} from "@axvdex/hooks/solana/utils/DeriveOutbidPDA";
import { processError } from "@axvdex/hooks/solana/utils/client";
import { getSale } from "@axvdex/hooks/solana/useOutbid";
import { useUserBalance } from "@axvdex/hooks/solana/useUserBalance";
import { SolanaSaleOperatorControlsProps } from "../SolanaSaleOperatorControls";

type NumberInput = string;
const ALLOWED_STATUSES_TO_ADD_INCENTIVES: SaleStatus[] = [
  "WaitingToStart",
  "WhitelistBidding",
  "Bidding",
  "BonusBidding",
];

function AddIncentive({ outbidSale }: SolanaSaleOperatorControlsProps) {
  const { i18 } = useLanguage();
  const [incentiveType, setIncentiveType] = useState<"specific" | "soldOut">("specific");
  const [incentiveAsset, setIncentiveAsset] = useState<"token" | "nft">("token");
  const [tokenIncentive, setTokenIncentive] = useState(null);
  const [specificBidIncentive, setSpecificBidIncentive] = useState<NumberInput>("");
  const [tokenIncentiveAmount, setTokenIncentiveAmount] = useState<NumberInput>();
  const [nftIncentiveImgURL, setNftIncentiveImgURL] = useState("");
  const [selectedSaleAddress, setSelectedSaleAddress] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { connection } = useConnection();
  const { assetBalances } = useUserBalance();

  // To display the existing incentives
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [loadingSale, setLoadingSale] = useState(false);

  useEffect(() => {
    setSelectedSaleAddress(getSaleRunning());
  }, []);

  useEffect(() => {
    if (!selectedSaleAddress) return;

    // Fetch the sale
    const saleAccount = new PublicKey(selectedSaleAddress);
    const fetchSale = async () => {
      if (!selectedSaleAddress) return;
      setLoadingSale(true);
      try {
        const sale = await getSale(connection, saleAccount);
        setSelectedSale(sale);
        setSpecificBidIncentive(sale.saleState.bidsCount.toString());
      } catch (error: unknown) {
        console.error(error);
        const msg = error instanceof Error ? error.message : "An error occurred";
        dispatch(sendToast({ type: "tx-fail", info: { msg, toastID: "" + new Date().getTime() } }));
      } finally {
        setLoadingSale(false);
      }
    };
    fetchSale();

    // Subscribe to the sale incentives accounts to update the UI when they change
    const [specificIncentivesAccount] = findSpecificBidsIncentives(saleAccount);
    const clientSubscriptionIdSpecific = connection.onAccountChange(specificIncentivesAccount, async () => {
      console.debug("Specific incentives account changed | Updating specific incentives ...");
      fetchSale();
    });
    const [soldOutIncentivesAccount] = findSoldOutBidderIncentives(saleAccount);
    const clientSubscriptionIdSoldOut = connection.onAccountChange(soldOutIncentivesAccount, async () => {
      console.debug("Sold out incentives account changed | Updating sold out incentives ...");
      fetchSale();
    });

    // Unsubscribe from the sale when the component unmounts
    return () => {
      connection.removeAccountChangeListener(clientSubscriptionIdSpecific);
      connection.removeAccountChangeListener(clientSubscriptionIdSoldOut);
    };
  }, [selectedSaleAddress]);

  const getSaleRunning = () => {
    for (const saleAddress of Object.keys(outbidSale.salesState)) {
      if (ALLOWED_STATUSES_TO_ADD_INCENTIVES.includes(outbidSale.salesState[saleAddress]?.status)) {
        return saleAddress;
      }
    }
    return Object.keys(outbidSale.salesState).pop();
  };

  return (
    <div style={{ marginTop: "1em" }}>
      {selectedSaleAddress && (
        <h2>
          <Button
            onClick={() => {
              const prevAddressAddress = Object.keys(outbidSale.salesState).find(
                key => outbidSale.salesState[key].id === outbidSale.salesState[selectedSaleAddress].id - 1
              );
              if (prevAddressAddress) setSelectedSaleAddress(prevAddressAddress);
            }}
          >
            {"<"}
          </Button>
          Round {outbidSale.salesState[selectedSaleAddress].id}
          <Button
            onClick={() => {
              const nextAddressAddress = Object.keys(outbidSale.salesState).find(
                key => outbidSale.salesState[key].id === outbidSale.salesState[selectedSaleAddress].id + 1
              );
              if (nextAddressAddress) setSelectedSaleAddress(nextAddressAddress);
            }}
          >
            {">"}
          </Button>
        </h2>
      )}

      {ALLOWED_STATUSES_TO_ADD_INCENTIVES.includes(outbidSale.salesState[selectedSaleAddress]?.status) ? (
        <>
          <div style={{ marginBottom: "1em" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: ".5em" }}>
              <input
                type="radio"
                id="incentive"
                name="incentive"
                style={{ marginRight: ".75em" }}
                onClick={() => setIncentiveType("specific")}
                checked={incentiveType === "specific"}
              />{" "}
              Specific Bid Incentive
              <input
                style={{ maxWidth: "10em", marginLeft: "1em" }}
                value={specificBidIncentive}
                placeholder=""
                onChange={e => setSpecificBidIncentive(e.target.value)}
              />
            </div>
            <div
              className="withGradientBorderBottom"
              style={{ display: "flex", alignItems: "center", paddingBottom: "1em" }}
            >
              <input
                type="radio"
                id="incentive"
                name="incentive"
                style={{ marginRight: ".75em" }}
                onClick={() => setIncentiveType("soldOut")}
                checked={incentiveType === "soldOut"}
              />{" "}
              Sold Out Incentive
            </div>

            <div style={{ marginTop: "1em" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: ".5em" }}>
                <input
                  type="radio"
                  id="incentive_asset"
                  name="incentive_asset"
                  style={{ marginRight: ".75em" }}
                  onClick={() => setIncentiveAsset("token")}
                  checked={incentiveAsset === "token"}
                />{" "}
                Token
                <div style={{ marginLeft: ".75em", display: "flex", width: "100%" }}>
                  <CustomSelect
                    labelText={""}
                    hiddenLabel={true}
                    items={Object.values(outbidSale.assets)
                      .map((asset: OutbidAsset) => {
                        return {
                          value: asset.mint.toBase58(),
                          label: asset.symbol,
                          id: asset.mint.toBase58(),
                          optionPrefix: <img src={imgSanitize(asset.symbol)} alt={""} />,
                        };
                      })
                      .filter(asset => asset)}
                    placeholder={i18("Select token", "trade.form.fromToken.select.placeholder")}
                    value={tokenIncentive}
                    onChange={val => setTokenIncentive(val)}
                    name={""}
                  />
                  <input
                    style={{ maxWidth: "10em", marginLeft: "1em" }}
                    onChange={e => setTokenIncentiveAmount(e.target.value)}
                    value={tokenIncentiveAmount}
                  />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", marginBottom: ".5em" }}>
                <input
                  type="radio"
                  id="incentive_asset"
                  name="incentive_asset"
                  style={{ marginRight: ".75em" }}
                  onClick={() => setIncentiveAsset("nft")}
                  checked={incentiveAsset === "nft"}
                  disabled={true /* TODO: Implement NFT Incentive */}
                />{" "}
                NFT
                <input
                  style={{ maxWidth: "100%", marginLeft: "1em" }}
                  onChange={e => setNftIncentiveImgURL(e.target.value)}
                  value={nftIncentiveImgURL}
                />
              </div>
            </div>
          </div>

          <Button
            btnColor="gradient"
            onClick={async () => {
              setIsLoading(true);
              try {
                // Check if the operator has enough balance
                if (
                  "token" === incentiveAsset &&
                  assetBalances[tokenIncentive.id] &&
                  assetBalances[tokenIncentive.id] < parseFloat(tokenIncentiveAmount)
                ) {
                  throw new Error("Not enough balance");
                }
                // Build the transaction
                const saleAccount = new PublicKey(selectedSaleAddress);
                const [specificBidsIncentives] = findSpecificBidsIncentives(saleAccount);
                const [transferAuthority] = findTransferAuthority(saleAccount);
                const [soldOutBidderIncentives] = findSoldOutBidderIncentives(saleAccount);
                const tx = new Transaction();
                if ("specific" === incentiveType) {
                  if ("" === specificBidIncentive) throw new Error("Bid ID not set");
                  if ("token" === incentiveAsset) {
                    if (tokenIncentive == null || "" === tokenIncentiveAmount) {
                      throw new Error("Token incentive not selected or amount not set");
                    }
                    const mint = new PublicKey(tokenIncentive.id);
                    const destTokenAccount = getAssociatedTokenAddressSync(mint, transferAuthority, true);
                    // If this account doesn't exist, it mean the operator doesnt' have a token account
                    const srcTokenAccount = getAssociatedTokenAddressSync(mint, window.solanaClient.publicKey);
                    const amount = new BN(
                      Math.floor(
                        parseFloat(tokenIncentiveAmount) * Math.pow(10, outbidSale.assets[tokenIncentive.id].decimals)
                      )
                    );

                    const specificBidsIncentivesIx = createAddSpecificBidsIncentivesInstruction(
                      {
                        saleAccount: saleAccount,
                        saleAuthority: window.solanaClient.publicKey,
                        transferAuthority,
                        specificBidsIncentives,
                        incentiveDest: destTokenAccount,
                        incentiveSrc: srcTokenAccount,
                        incentiveMint: mint,
                      },
                      {
                        addSpecificBidsIncentivesArgs: {
                          bidId: new BN(specificBidIncentive),
                          amounts: [amount],
                        },
                      }
                    );
                    console.log("Sending specific bid incentives", specificBidsIncentivesIx);
                    tx.add(specificBidsIncentivesIx);
                  }
                  if ("nft" === incentiveAsset && "" !== nftIncentiveImgURL) {
                    // TODO: NFT Incentive
                    throw new Error("NFT integration comming soon!");
                  }
                } else if ("soldOut" === incentiveType) {
                  const mint = new PublicKey(tokenIncentive.id);
                  const destTokenAccount = getAssociatedTokenAddressSync(mint, transferAuthority, true);
                  // If this account doesn't exist, it mean the operator doesnt' have a token account
                  const srcTokenAccount = getAssociatedTokenAddressSync(mint, window.solanaClient.publicKey);
                  const amount = new BN(
                    Math.floor(
                      parseFloat(tokenIncentiveAmount) * Math.pow(10, outbidSale.assets[tokenIncentive.id].decimals)
                    )
                  );
                  const soldOutBidderIncentivesIx = createAddSoldOutBidderIncentivesInstruction(
                    {
                      saleAccount: saleAccount,
                      saleAuthority: window.solanaClient.publicKey,
                      transferAuthority,
                      soldOutBidderIncentives,
                      incentiveDest: destTokenAccount,
                      incentiveSrc: srcTokenAccount,
                      incentiveMint: mint,
                    },
                    {
                      addSoldOutBidderIncentivesArgs: {
                        amounts: [amount],
                      },
                    }
                  );
                  console.log("Sending sold out bid incentives", soldOutBidderIncentivesIx);
                  tx.add(soldOutBidderIncentivesIx);
                }

                // Send the transaction
                try {
                  // const signature = await sendWithPhantomAndDebugTransaction(tx, connection, errorFromCode);
                  const signature = await window.solanaClient.sendTransaction(tx, connection);
                  await connection.confirmTransaction(signature, "finalized");
                } catch (error) {
                  console.error(processError(error.message, errorFromCode));
                  throw error;
                }
              } catch (error: unknown) {
                console.error(error);
                const msg = error instanceof Error ? error.message : "An error occurred";
                dispatch(sendToast({ type: "tx-fail", info: { msg, toastID: "" + new Date().getTime() } }));
              } finally {
                setIsLoading(false);
              }
            }}
          >
            {isLoading ? "Executing ..." : "Add"}
          </Button>
          <div className="withGradientBorderBottom" style={{ marginTop: "1em" }}></div>
        </>
      ) : (
        <p> Sale Ended </p>
      )}

      <div style={{ marginTop: "1em" }}>
        <h2 style={{ marginBottom: "0em" }}>
          {loadingSale ? "Loading Existing Incentives ..." : "Existing Incentives"}
        </h2>
        {!loadingSale && selectedSale && (
          <>
            <small>Current Bid ID: {selectedSale.saleState.bidsCount.toString()}</small>
            <table className={clsx("table")}>
              <thead>
                <tr>
                  <th style={{ width: "5em", textAlign: "center" }}>-</th>
                  <th style={{ textAlign: "center" }}>Bid ID</th>
                  <th style={{ textAlign: "center" }}>Assets</th>
                </tr>
              </thead>
              <tbody>
                {selectedSale.specificBidIncentives && (
                  <>
                    {Array.from(selectedSale.specificBidIncentives.entries()).map(
                      ([bidCount, incentives], index: number) => (
                        <tr key={index}>
                          <td style={{ width: "5em" }}>
                            <Button
                              icon={<Trash />}
                              disabled={true}
                              onClick={async () => {
                                // TODO: Not implemented on the contract ?
                              }}
                            ></Button>
                          </td>
                          <td>{bidCount}</td>
                          <td>
                            {incentives.map((incentive: DigitalAsset, index: number) => (
                              <div key={index} className="inlineFlexbox" style={{ marginRight: "1em" }}>
                                {incentive.isAsset && (
                                  <>
                                    <span>
                                      {BigNumber(incentive.amount)
                                        .div(Math.pow(10, outbidSale.assets[incentive.mint.toBase58()].decimals))
                                        .toString(10)}
                                    </span>
                                    <img
                                      style={{ width: "2em" }}
                                      src={imgSanitize(outbidSale.assets[incentive.mint.toBase58()].symbol)}
                                      alt={""}
                                    />
                                  </>
                                )}
                                {incentive.isNFT && (
                                  <>
                                    <span>NFT</span>
                                  </>
                                )}
                              </div>
                            ))}
                          </td>
                        </tr>
                      )
                    )}
                  </>
                )}
                {selectedSale.soldOutBidderIncentives && selectedSale.soldOutBidderIncentives.length > 0 && (
                  <tr>
                    <td style={{ width: "5em" }}>
                      <Button
                        icon={<Trash />}
                        disabled={true}
                        onClick={async () => {
                          // TODO: Not implemented on the contract ?
                        }}
                      ></Button>
                    </td>
                    <td>-</td>
                    <td>
                      {selectedSale.soldOutBidderIncentives.map((incentive: DigitalAsset, index: number) => (
                        <div key={index} className="inlineFlexbox" style={{ marginRight: "1em" }}>
                          {incentive.isAsset && (
                            <>
                              <span>
                                {BigNumber(incentive.amount)
                                  .div(Math.pow(10, outbidSale.assets[incentive.mint.toBase58()].decimals))
                                  .toString(10)}
                              </span>
                              <img
                                style={{ width: "2em" }}
                                src={imgSanitize(outbidSale.assets[incentive.mint.toBase58()].symbol)}
                                alt={""}
                              />
                            </>
                          )}
                          {incentive.isNFT && (
                            <>
                              <span>NFT</span>
                            </>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default AddIncentive;
